import { useState, useEffect, useCallback, useRef } from 'react';
import { UserProviderType, UserType } from 'src/types/user';
import { ProviderType } from 'src/types/provider';
import { SelectValueType } from 'src/components/Select';
import { OnChangeType } from '../../types/global';
import { EditKeysType } from './constants';

const getActiveProvidersSorted = (providersListData: ProviderType[]): ProviderType[] =>
    providersListData
        .filter((provider) => provider.ACTIVE === 1)
        .sort((a, b) => (a.COMPANY_NAME || '').localeCompare(b.COMPANY_NAME || '', 'cs'));

export const buildAllActiveUserProviders = (
    providersListData: ProviderType[],
    sourceUserProviders: UserProviderType[] = []
): UserProviderType[] =>
    getActiveProvidersSorted(providersListData).map((provider) => {
        const existing = sourceUserProviders.find(
            (userProvider) => userProvider.PROVIDER_ESHOP_KEY === provider.ESHOP_KEY
        );
        return (
            existing ?? {
                PROVIDER_ESHOP_KEY: String(provider.ESHOP_KEY),
                NOTIFICATION_ACTIVE: 1,
            }
        );
    });

export const hasAllActiveProviders = (
    userProviders: UserProviderType[],
    activeProviderKeys: string[]
): boolean => {
    if (!activeProviderKeys.length) {
        return false;
    }
    const assignedKeys = userProviders.map((provider) => provider.PROVIDER_ESHOP_KEY);
    return activeProviderKeys.every((key) => assignedKeys.includes(key));
};

type UseAllProvidersFieldParams = {
    onChange: OnChangeType<EditKeysType>;
    editData: UserType;
    oreginalData: UserType | null;
    providersListData: ProviderType[];
    newUser: boolean;
    loggedSkladonUser?: number;
    disableEdit: boolean;
    enabled: boolean;
};

const useAllProvidersField = (params: UseAllProvidersFieldParams) => {
    const {
        onChange,
        editData,
        oreginalData,
        providersListData,
        newUser,
        loggedSkladonUser,
        disableEdit,
        enabled,
    } = params;

    const [allProviders, setAllProviders] = useState<number>(newUser ? 1 : 0);
    const prevIsSkladonUserRef = useRef<boolean | null>(null);

    const isSkladonUser = Number(editData.SKLADON_USER) === 1;
    // Visible whenever the Skladon-employee switch is visible (logged Skladon user), not only when value is Ano
    const showAllProvidersField = enabled && !!loggedSkladonUser;
    const allProvidersFieldDisabled = disableEdit || !isSkladonUser;

    useEffect(() => {
        if (!enabled || !loggedSkladonUser) {
            return;
        }

        const wasSkladonUser = prevIsSkladonUserRef.current;
        prevIsSkladonUserRef.current = isSkladonUser;

        if (wasSkladonUser === true && !isSkladonUser) {
            setAllProviders(0);
            if ((editData.USER_PROVIDERS ?? []).length > 0) {
                onChange('USER_PROVIDERS', [], true);
            }
            return;
        }

        if (wasSkladonUser === false && isSkladonUser) {
            setAllProviders(1);
            return;
        }

        if (!isSkladonUser) {
            setAllProviders(0);
            return;
        }

        if (newUser) {
            return;
        }

        if (allProviders === 1) {
            return;
        }

        const activeProviderKeys = getActiveProvidersSorted(providersListData).map((provider) =>
            String(provider.ESHOP_KEY)
        );
        const userProviders = editData.USER_PROVIDERS ?? [];
        setAllProviders(hasAllActiveProviders(userProviders, activeProviderKeys) ? 1 : 0);
    }, [
        enabled,
        loggedSkladonUser,
        isSkladonUser,
        newUser,
        allProviders,
        editData.USER_PROVIDERS,
        providersListData,
        onChange,
    ]);

    useEffect(() => {
        if (!enabled || !isSkladonUser || allProviders !== 1 || !providersListData.length) {
            return;
        }

        const activeProviderKeys = getActiveProvidersSorted(providersListData).map((provider) =>
            String(provider.ESHOP_KEY)
        );
        const currentProviders = editData.USER_PROVIDERS ?? [];
        const currentKeys = currentProviders.map((provider) => provider.PROVIDER_ESHOP_KEY);
        const needsAssign =
            activeProviderKeys.length > 0 &&
            (currentKeys.length !== activeProviderKeys.length ||
                !activeProviderKeys.every((key) => currentKeys.includes(key)));

        if (needsAssign) {
            const sourceUserProviders = oreginalData?.USER_PROVIDERS ?? currentProviders;
            onChange('USER_PROVIDERS', buildAllActiveUserProviders(providersListData, sourceUserProviders), false);
        }
    }, [
        enabled,
        isSkladonUser,
        allProviders,
        providersListData,
        editData.USER_PROVIDERS,
        oreginalData?.USER_PROVIDERS,
        onChange,
    ]);

    const onChangeAllProviders = useCallback(
        (value: SelectValueType | SelectValueType[]) => {
            if (Array.isArray(value)) {
                return;
            }

            const newValue = Number(value) === 1 ? 1 : 0;
            setAllProviders(newValue);

            if (newValue === 1) {
                const sourceUserProviders = oreginalData?.USER_PROVIDERS ?? editData.USER_PROVIDERS ?? [];
                onChange(
                    'USER_PROVIDERS',
                    buildAllActiveUserProviders(providersListData, sourceUserProviders),
                    false
                );
            } else {
                onChange('USER_PROVIDERS', [], true);
            }
        },
        [onChange, providersListData, oreginalData?.USER_PROVIDERS, editData.USER_PROVIDERS]
    );

    return {
        allProviders,
        setAllProviders,
        isSkladonUser,
        showAllProvidersField,
        allProvidersFieldDisabled,
        onChangeAllProviders,
    };
};

export default useAllProvidersField;
